const CTA_VALUES = new Set(['none', 'primary', 'secondary', 'all']);
const SECTION_MARKER_REGEX = /\[\[SECTION:\s*([^\]]+)\s*\]\]/gi;
const CTA_MARKER_REGEX = /\[\[CTA:\s*([^\]]+)\s*\]\]/gi;
const makeSectionMarkerRegex = () => /\[\[SECTION:\s*([^\]]+)\s*\]\]/gi;
const makeCtaMarkerRegex = () => /\[\[CTA:\s*([^\]]+)\s*\]\]/gi;
const createSectionId = () =>
  `section-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

const normalizeCtaValue = value => {
  const normalized = typeof value === 'string' ? value.toLowerCase() : '';
  return CTA_VALUES.has(normalized) ? normalized : 'none';
};

const safeDateValue = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const resolveTemplateLanguageCode = template => {
  if (!template || typeof template !== 'object') return '';
  const direct =
    template.language ||
    template.languageCode ||
    template.language_code ||
    template.code;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const keyCode =
    template.languageKey?.code ||
    template.LanguageKey?.code ||
    template.language_key?.code ||
    template.languagekey?.code;
  if (typeof keyCode === 'string' && keyCode.trim()) return keyCode.trim();
  return '';
};

const stripLayoutMarkers = text => {
  if (typeof text !== 'string') return '';
  const sectionRegex = makeSectionMarkerRegex();
  const ctaRegex = makeCtaMarkerRegex();
  return text
    .replace(sectionRegex, '')
    .replace(ctaRegex, '')
    .trim();
};

const parseCtaMarker = raw => {
  const parsed = {
    type: 'primary',
    text: '',
    url: '',
  };
  if (!raw || typeof raw !== 'string') return parsed;
  const parts = raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  parts.forEach(part => {
    const lower = part.toLowerCase();
    if (['primary', 'secondary', 'all'].includes(lower)) {
      parsed.type = lower;
      return;
    }
    const idx = part.indexOf(':');
    if (idx < 0) return;
    const key = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).trim();
    if (key === 'text') parsed.text = value;
    if (key === 'url') parsed.url = value;
  });
  return parsed;
};

const extractIntroCtaMarkers = rawContent => {
  const content = typeof rawContent === 'string' ? rawContent : '';
  const sectionRegex = makeSectionMarkerRegex();
  const ctaRegex = makeCtaMarkerRegex();
  const sectionMatch = sectionRegex.exec(content);
  const introBlock = sectionMatch
    ? content.slice(0, sectionMatch.index)
    : content;
  return Array.from(introBlock.matchAll(ctaRegex)).map(match =>
    parseCtaMarker(match?.[1] || '')
  );
};

const extractCtaMarkersWithPlacement = rawContent => {
  const content = typeof rawContent === 'string' ? rawContent : '';
  const sectionRegex = makeSectionMarkerRegex();
  const ctaRegex = makeCtaMarkerRegex();
  const sectionMatches = Array.from(content.matchAll(sectionRegex));
  const ctaMatches = Array.from(content.matchAll(ctaRegex));
  if (!ctaMatches.length) return [];

  return ctaMatches.map(match => {
    const marker = parseCtaMarker(match?.[1] || '');
    const markerIndex = Number(match?.index ?? -1);
    const sectionIndex = sectionMatches.reduce((acc, section, idx) => {
      const sectionPos = Number(section?.index ?? -1);
      if (sectionPos >= 0 && sectionPos < markerIndex) return idx;
      return acc;
    }, -1);
    const placement =
      sectionIndex >= 0 ? `after-section-${sectionIndex}` : 'after-intro';
    return {
      ...marker,
      placement,
    };
  });
};

const parseSectionsFromContent = rawContent => {
  const content = typeof rawContent === 'string' ? rawContent : '';
  const sectionRegex = makeSectionMarkerRegex();
  const ctaRegex = makeCtaMarkerRegex();
  const matches = Array.from(content.matchAll(sectionRegex));
  if (!matches.length) {
    return {
      intro: stripLayoutMarkers(content),
      sections: [],
    };
  }

  const intro = content.slice(0, matches[0].index).trim();
  const sections = [];

  matches.forEach((match, index) => {
    const title = String(match[1] || '').trim() || `Section ${index + 1}`;
    const start = match.index + match[0].length;
    const end = matches[index + 1]?.index ?? content.length;
    const block = content.slice(start, end);
    const ctaMatches = Array.from(block.matchAll(ctaRegex));

    let hasPrimary = false;
    let hasSecondary = false;
    let ctaText = '';
    let ctaUrl = '';
    let ctaTextSecondary = '';
    let ctaUrlSecondary = '';

    ctaMatches.forEach(ctaMatch => {
      const parsed = parseCtaMarker(ctaMatch[1] || '');
      const markerType = parsed.type || 'primary';
      if (markerType === 'secondary') {
        hasSecondary = true;
        if (parsed.text) ctaTextSecondary = parsed.text;
        if (parsed.url) ctaUrlSecondary = parsed.url;
        return;
      }
      if (markerType === 'all') {
        hasPrimary = true;
        hasSecondary = true;
        if (parsed.text) {
          if (!ctaText) ctaText = parsed.text;
          if (!ctaTextSecondary) ctaTextSecondary = parsed.text;
        }
        if (parsed.url) {
          if (!ctaUrl) ctaUrl = parsed.url;
          if (!ctaUrlSecondary) ctaUrlSecondary = parsed.url;
        }
        return;
      }
      hasPrimary = true;
      if (parsed.text) ctaText = parsed.text;
      if (parsed.url) ctaUrl = parsed.url;
    });

    let cta = 'none';
    if (hasPrimary && hasSecondary) cta = 'all';
    else if (hasSecondary) cta = 'secondary';
    else if (hasPrimary) cta = 'primary';

    sections.push({
      title,
      body: stripLayoutMarkers(block),
      cta,
      ctaText,
      ctaUrl,
      ctaTextSecondary,
      ctaUrlSecondary,
    });
  });

  return { intro, sections };
};

const buildSectionTitle = (section, index, base, settings) => {
  const fallbackColor = settings?.linkColor || '#00519e';
  const fallbackStyles = Array.isArray(base?.styles) ? base.styles : ['bold'];
  return {
    id: base?.id || createSectionId(),
    text: section?.title || base?.text || `Section ${index + 1}`,
    body: stripLayoutMarkers(section?.body),
    color: base?.color || fallbackColor,
    fontSize: Number(base?.fontSize) || 18,
    styles: fallbackStyles,
    level: base?.level,
  };
};

const getDefaultCtaStyle = settings => ({
  backgroundColor: settings?.linkColor || '#00519e',
  textColor: '#ffffff',
  fontSize: 14,
  paddingX: 20,
  paddingY: 10,
  borderRadius: 6,
});

const buildCtaFromStyle = (style, text, url, placement, settings) => {
  const fallback = getDefaultCtaStyle(settings);
  const fallbackStyles = Array.isArray(style?.styles) ? style.styles : ['bold'];
  return {
    id: style?.id || createSectionId(),
    text: text || style?.text || 'Call to action',
    url: url || style?.url || '#',
    backgroundColor: style?.backgroundColor || fallback.backgroundColor,
    textColor: style?.textColor || fallback.textColor,
    fontSize: Number(style?.fontSize) || fallback.fontSize,
    paddingX:
      style?.paddingX !== undefined ? style.paddingX : fallback.paddingX,
    paddingY:
      style?.paddingY !== undefined ? style.paddingY : fallback.paddingY,
    borderRadius:
      style?.borderRadius !== undefined ? style.borderRadius : fallback.borderRadius,
    styles: fallbackStyles,
    placement,
  };
};

const buildCtaButtonsFromSections = (ctaStyles, sections, settings) => {
  if (!Array.isArray(sections) || sections.length === 0) return [];
  const styles = Array.isArray(ctaStyles) && ctaStyles.length
    ? ctaStyles
    : [];
  const primaryStyle = styles[0] || null;
  const secondaryStyle = styles[1] || styles[0] || null;

  const buttons = [];
  sections.forEach((section, index) => {
    const ctaValue = normalizeCtaValue(
      section?.cta || section?.ctaSlot || section?.ctaPlacement
    );
    if (ctaValue === 'none') return;
    const placement = `after-section-${index}`;

    if (ctaValue === 'all') {
      const primaryText = section?.ctaText || section?.ctaLabel;
      const primaryUrl = section?.ctaUrl;
      const secondaryText =
        section?.ctaTextSecondary || section?.ctaText || section?.ctaLabel;
      const secondaryUrl = section?.ctaUrlSecondary || section?.ctaUrl;
      buttons.push(
        buildCtaFromStyle(primaryStyle, primaryText, primaryUrl, placement, settings)
      );
      buttons.push(
        buildCtaFromStyle(
          secondaryStyle,
          secondaryText,
          secondaryUrl,
          placement,
          settings
        )
      );
      return;
    }

    if (ctaValue === 'secondary') {
      buttons.push(
        buildCtaFromStyle(
          secondaryStyle,
          section?.ctaText || section?.ctaLabel,
          section?.ctaUrl,
          placement,
          settings
        )
      );
      return;
    }

    buttons.push(
      buildCtaFromStyle(
        primaryStyle,
        section?.ctaText || section?.ctaLabel,
        section?.ctaUrl,
        placement,
        settings
      )
    );
  });

  return buttons;
};

const buildCtaButtonsFromIntro = (ctaStyles, markers, settings) => {
  if (!Array.isArray(markers) || markers.length === 0) return [];
  const styles = Array.isArray(ctaStyles) && ctaStyles.length
    ? ctaStyles
    : [];
  const primaryStyle = styles[0] || null;
  const secondaryStyle = styles[1] || styles[0] || null;
  const placement = 'after-intro';
  const buttons = [];

  markers.forEach(marker => {
    const ctaValue = normalizeCtaValue(marker?.type);
    if (ctaValue === 'none') return;
    if (ctaValue === 'all') {
      buttons.push(
        buildCtaFromStyle(primaryStyle, marker?.text, marker?.url, placement, settings)
      );
      buttons.push(
        buildCtaFromStyle(secondaryStyle, marker?.text, marker?.url, placement, settings)
      );
      return;
    }
    if (ctaValue === 'secondary') {
      buttons.push(
        buildCtaFromStyle(secondaryStyle, marker?.text, marker?.url, placement, settings)
      );
      return;
    }
    buttons.push(
      buildCtaFromStyle(primaryStyle, marker?.text, marker?.url, placement, settings)
    );
  });

  return buttons;
};

const buildCtaButtonsFromMarkers = (ctaStyles, markers, settings) => {
  if (!Array.isArray(markers) || markers.length === 0) return [];
  const styles = Array.isArray(ctaStyles) && ctaStyles.length
    ? ctaStyles
    : [];
  const primaryStyle = styles[0] || null;
  const secondaryStyle = styles[1] || styles[0] || null;
  const buttons = [];

  markers.forEach(marker => {
    const ctaValue = normalizeCtaValue(marker?.type);
    const placement =
      typeof marker?.placement === 'string' && marker.placement.trim()
        ? marker.placement
        : 'after-intro';
    if (ctaValue === 'none') return;
    if (ctaValue === 'all') {
      buttons.push(
        buildCtaFromStyle(primaryStyle, marker?.text, marker?.url, placement, settings)
      );
      buttons.push(
        buildCtaFromStyle(secondaryStyle, marker?.text, marker?.url, placement, settings)
      );
      return;
    }
    if (ctaValue === 'secondary') {
      buttons.push(
        buildCtaFromStyle(secondaryStyle, marker?.text, marker?.url, placement, settings)
      );
      return;
    }
    buttons.push(
      buildCtaFromStyle(primaryStyle, marker?.text, marker?.url, placement, settings)
    );
  });

  return buttons;
};

export const applyTextTemplateToSettings = (settings, textTemplate, options = {}) => {
  if (!textTemplate) return { settings, changedKeys: [] };
  const nextSettings = { ...(settings || {}) };
  const changedKeys = [];
  const nextTemplateLanguage = resolveTemplateLanguageCode(textTemplate);

  const parsedFromContent = parseSectionsFromContent(textTemplate.content);
  const fallbackSections = parsedFromContent.sections;
  const introCtaMarkers = extractIntroCtaMarkers(textTemplate.content);
  const positionedMarkers = extractCtaMarkersWithPlacement(textTemplate.content);
  const nextContent = parsedFromContent.intro;
  const nextRawContent =
    typeof textTemplate.content === 'string' ? textTemplate.content : '';
  if (nextSettings.textTemplateContent !== nextContent) {
    nextSettings.textTemplateContent = nextContent;
    changedKeys.push('textTemplateContent');
  }
  if (nextSettings.textTemplateRawContent !== nextRawContent) {
    nextSettings.textTemplateRawContent = nextRawContent;
    changedKeys.push('textTemplateRawContent');
  }

  const sections =
    Array.isArray(fallbackSections) && fallbackSections.length > 0
      ? fallbackSections
      : Array.isArray(textTemplate.sections)
        ? textTemplate.sections
        : [];
  if (sections.length > 0) {
    const existingTitles = Array.isArray(nextSettings.sectionTitles)
      ? nextSettings.sectionTitles
      : [];
    const fallbackBase =
      existingTitles.length > 0
        ? existingTitles[existingTitles.length - 1]
        : null;
    nextSettings.sectionTitles = sections.map((section, index) => {
      const base = existingTitles[index] || fallbackBase || null;
      return buildSectionTitle(section, index, base, nextSettings);
    });
    changedKeys.push('sectionTitles');
  }

  const baseCtaStyles =
    Array.isArray(nextSettings.ctaStyleSlots) && nextSettings.ctaStyleSlots.length
      ? nextSettings.ctaStyleSlots
      : Array.isArray(nextSettings.ctaButtons)
        ? nextSettings.ctaButtons
        : [];
  if (!Array.isArray(nextSettings.ctaStyleSlots) || nextSettings.ctaStyleSlots.length === 0) {
    nextSettings.ctaStyleSlots = baseCtaStyles.map(style =>
      style ? { ...style } : style
    );
    changedKeys.push('ctaStyleSlots');
  }

  if (positionedMarkers.length > 0) {
    nextSettings.ctaButtons = buildCtaButtonsFromMarkers(
      baseCtaStyles,
      positionedMarkers,
      nextSettings
    );
  } else {
    const ctaFromSections = buildCtaButtonsFromSections(
      baseCtaStyles,
      sections,
      nextSettings
    );
    const ctaFromIntro = buildCtaButtonsFromIntro(
      baseCtaStyles,
      introCtaMarkers,
      nextSettings
    );
    nextSettings.ctaButtons = [...ctaFromIntro, ...ctaFromSections];
  }
  changedKeys.push('ctaButtons');

  const mode =
    options.mode ||
    nextSettings.textTemplateLink?.mode ||
    'auto';
  nextSettings.textTemplateLink = {
    id: textTemplate.id,
    name: textTemplate.name,
    mode,
    templateUpdatedAt:
      safeDateValue(textTemplate.updatedAt || textTemplate.updated_at) || null,
    syncedAt: new Date().toISOString(),
  };
  changedKeys.push('textTemplateLink');

  if ((nextSettings.textTemplateLanguage || '') !== nextTemplateLanguage) {
    nextSettings.textTemplateLanguage = nextTemplateLanguage;
    changedKeys.push('textTemplateLanguage');
  }

  return { settings: nextSettings, changedKeys };
};

export const normalizeTextTemplateSections = sections => {
  if (!Array.isArray(sections)) return [];
  return sections
    .map(section => {
      if (!section) return null;
      const title =
        typeof section.title === 'string' && section.title.trim()
          ? section.title.trim()
          : '';
      const body = typeof section.body === 'string' ? section.body : '';
      if (!title) return null;
      return {
        title,
        body,
        cta: normalizeCtaValue(section.cta),
        ctaText:
          typeof section.ctaText === 'string' ? section.ctaText : '',
        ctaUrl: typeof section.ctaUrl === 'string' ? section.ctaUrl : '',
        ctaTextSecondary:
          typeof section.ctaTextSecondary === 'string'
            ? section.ctaTextSecondary
            : '',
        ctaUrlSecondary:
          typeof section.ctaUrlSecondary === 'string'
            ? section.ctaUrlSecondary
            : '',
      };
    })
    .filter(Boolean);
};

export const normalizeTextTemplateCta = normalizeCtaValue;
